import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import CtaSection from "@/components/shared/CtaSection";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import { GLOSSARY, GLOSSARY_CATEGORIES } from "@/data/glossary";

export default function GlossaryHub() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const filtered = GLOSSARY.filter((t) => {
    const matchCat = cat === "All" || t.category === cat;
    const matchQ = t.term.toLowerCase().includes(q.toLowerCase()) || t.definition.toLowerCase().includes(q.toLowerCase());
    return matchCat && matchQ;
  });

  return (
    <>
      <Seo
        title="GEO & AI Search Glossary"
        description="An extensive glossary of Generative Engine Optimization, AI search, semantic SEO, entity SEO, structured data, knowledge graph and LLM terms — defined by CompuBrain AI."
        path="/glossary"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Glossary", path: "/glossary" }])]}
      />
      <PageHero
        eyebrow="Glossary"
        titleLines={["The language of", "AI search."]}
        sub="Clear, citable definitions of the key GEO, AI search and semantic SEO terms every decision-maker should know."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Glossary" }]}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-10">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search terms..."
              data-testid="glossary-search"
              className="w-full bg-white border border-gray-200 rounded-full pl-11 pr-4 py-3 text-[15px] focus:outline-none focus:border-[#0B63F6] transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {["All", ...GLOSSARY_CATEGORIES].map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${cat === c ? "bg-[#111111] text-white" : "border border-gray-200 text-gray-600 hover:border-[#111111]"}`}>{c}</button>
            ))}
          </div>
        </div>

        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((t) => (
            <StaggerItem key={t.slug}>
              <Link to={`/glossary/${t.slug}`} data-testid={`glossary-card-${t.slug}`} className="group flex flex-col h-full rounded-2xl border border-gray-200 p-7 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.05)] transition-all duration-500">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#6E56CF]">{t.category}</span>
                <h2 className="font-display text-lg font-bold tracking-tight mt-3">{t.term}</h2>
                <p className="text-[15px] text-gray-600 mt-3 leading-relaxed flex-1 line-clamp-3">{t.definition}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#0B63F6]">Read definition <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
        {filtered.length === 0 && <p className="text-center text-gray-500 py-16">No terms match your search.</p>}
      </section>
      <CtaSection heading="Ready to apply these concepts?" sub="Let CompuBrain AI turn GEO theory into measurable visibility." />
    </>
  );
}
